import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  Typography,
  Avatar,
  Badge,
  LinearProgress,
} from "@material-ui/core";
import {
  Chat as ChatIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  SmartToy as SmartToyIcon,
  AccountTree as AccountTreeIcon,
  Store as StoreIcon,
  Api as ApiIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  CreditCard as CreditCardIcon,
  ContactMail as ContactMailIcon,
  ListAlt as ListAltIcon,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: "#f8fafb",
    minHeight: "calc(100vh - 64px)",
  },
  welcomeCard: {
    background: "linear-gradient(135deg, #10aa62 0%, #065f38 100%)",
    color: "white",
    borderRadius: 16,
    padding: theme.spacing(3, 4),
    marginBottom: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      top: -40,
      right: -40,
      width: 200,
      height: 200,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
    },
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: "#374151",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  card: {
    borderRadius: 14,
    padding: theme.spacing(2.5),
    cursor: "pointer",
    transition: "all 0.25s ease",
    border: "1px solid #e5e7eb",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
      borderColor: "#10aa62",
    },
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1f2937",
  },
  cardDescription: {
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 1.4,
  },
  cardValue: {
    fontWeight: 800,
    fontSize: 24,
    color: "#10aa62",
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  statChip: {
    background: "#f0fdf4",
    color: "#10aa62",
    fontWeight: 600,
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 20,
    border: "1px solid #d1fae5",
  },
  tokenBar: {
    borderRadius: 10,
    height: 8,
    marginTop: 4,
    backgroundColor: "#e5e7eb",
    "& .MuiLinearProgress-bar": {
      borderRadius: 10,
      background: "linear-gradient(90deg, #10aa62 0%, #059669 100%)",
    },
  },
}));

const ClientDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    contacts: 0,
    tickets: { open: 0, pending: 0, closed: 0 },
    campaigns: 0,
    flows: 0,
    channels: { connected: 0, total: 4 },
    tokens: { limit: 0, used: 0 },
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [contactsRes, ticketsRes, campaignsRes, flowsRes, channelsRes] =
          await Promise.allSettled([
            api.get("/contacts?pageNumber=1&limit=1"),
            api.get("/tickets-count"),
            api.get("/campaigns"),
            api.get("/flowbuilder"),
            api.get("/whatsapp"),
          ]);

        setStats((prev) => ({
          ...prev,
          contacts:
            contactsRes.status === "fulfilled"
              ? contactsRes.value?.data?.count || contactsRes.value?.data?.contacts?.length || 0
              : 0,
          tickets:
            ticketsRes.status === "fulfilled" && ticketsRes.value?.data
              ? ticketsRes.value.data
              : prev.tickets,
          campaigns:
            campaignsRes.status === "fulfilled"
              ? Array.isArray(campaignsRes.value?.data) ? campaignsRes.value.data.length : 0
              : 0,
          flows:
            flowsRes.status === "fulfilled"
              ? Array.isArray(flowsRes.value?.data?.flows) ? flowsRes.value.data.flows.length : 0
              : 0,
          channels: {
            connected:
              channelsRes.status === "fulfilled" && Array.isArray(channelsRes.value?.data)
                ? channelsRes.value.data.filter((w) => w.status === "CONNECTED").length
                : 0,
            total: 4,
          },
        }));

        // Fetch AI tokens
        try {
          const aiRes = await api.get(`/companies/${user?.companyId}/ai/usage`);
          if (aiRes.data) {
            setStats((prev) => ({
              ...prev,
              tokens: {
                limit: aiRes.data.limit || 0,
                used: aiRes.data.used || 0,
              },
            }));
          }
        } catch {
          // AI usage endpoint may not exist
        }
      } catch (err) {
        console.error("Dashboard stats error:", err);
      }
    };

    fetchStats();
  }, [user?.companyId]);

  const cards = [
    {
      title: "Inbox",
      description: "Conversaciones con tus clientes por WhatsApp, Facebook e Instagram",
      icon: <ChatIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#10aa62",
      path: "/tickets",
      value: stats.tickets.open || 0,
      valueLabel: "activas",
    },
    {
      title: "Contactos",
      description: "Directorio de todos tus clientes y prospectos",
      icon: <PeopleIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#3b82f6",
      path: "/contacts",
      value: stats.contacts,
      valueLabel: "contactos",
    },
    {
      title: "Campanas",
      description: "Envia mensajes masivos a tus listas de contactos",
      icon: <CampaignIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#f59e0b",
      path: "/campaigns",
      value: stats.campaigns,
      valueLabel: "campanas",
    },
    {
      title: "Listas de Contactos",
      description: "Organiza tus contactos en listas para campanas",
      icon: <ListAltIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#8b5cf6",
      path: "/contact-lists",
    },
    {
      title: "Inteligencia Artificial",
      description: "Entrena tu IA, sube documentos y configura respuestas automaticas",
      icon: <SmartToyIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#ec4899",
      path: "/prompts",
      extra: stats.tokens.limit > 0
        ? `${((stats.tokens.used / stats.tokens.limit) * 100).toFixed(0)}% tokens usados`
        : null,
    },
    {
      title: "FlowBuilder",
      description: "Disena flujos de conversacion automatizados con 202 nodos",
      icon: <AccountTreeIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#06b6d4",
      path: "/flowbuilders",
      value: stats.flows,
      valueLabel: "flujos",
    },
    {
      title: "Conectar APIs",
      description: "Conecta WhatsApp Business, Facebook Messenger e Instagram",
      icon: <ApiIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#10b981",
      path: "/config-apis",
      value: stats.channels.connected,
      valueLabel: `de ${stats.channels.total} canales`,
    },
    {
      title: "Informes",
      description: "Reportes de atendimientos, tiempos y NPS",
      icon: <AssessmentIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#14b8a6",
      path: "/reports",
    },
    {
      title: "Mercado",
      description: "Addons y modulos para potenciar tu cuenta",
      icon: <StoreIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#6366f1",
      path: "/marketplace",
    },
    {
      title: "Membresia",
      description: "Tu plan actual, limites y opciones de upgrade",
      icon: <CreditCardIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#0ea5e9",
      path: "/subscription",
    },
    {
      title: "Configuraciones",
      description: "Opciones del sistema, SLA, horarios y mensajes",
      icon: <SettingsIcon style={{ fontSize: 28, color: "white" }} />,
      color: "#64748b",
      path: "/settings",
    },
  ];

  const tokenPercent =
    stats.tokens.limit > 0
      ? Math.min((stats.tokens.used / stats.tokens.limit) * 100, 100)
      : 0;

  return (
    <div className={classes.root}>
      {/* Welcome Card */}
      <Paper className={classes.welcomeCard} elevation={0}>
        <Typography variant="h5" style={{ fontWeight: 800, marginBottom: 4 }}>
          Hola {user?.name || "Cliente"} 👋
        </Typography>
        <Typography variant="body2" style={{ opacity: 0.85 }}>
          Bienvenido a tu panel de control. Aqui puedes gestionar tu negocio.
        </Typography>
        <div className={classes.statsRow} style={{ marginTop: 16 }}>
          <span className={classes.statChip} style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none" }}>
            {stats.channels.connected}/{stats.channels.total} canales conectados
          </span>
          <span className={classes.statChip} style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none" }}>
            {stats.contacts} contactos
          </span>
          {stats.tokens.limit > 0 && (
            <span className={classes.statChip} style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none" }}>
              {stats.tokens.limit - stats.tokens.used} tokens IA restantes
            </span>
          )}
        </div>
      </Paper>

      {/* AI Tokens Bar (if AI enabled) */}
      {stats.tokens.limit > 0 && (
        <Paper style={{ borderRadius: 12, padding: "16px 20px", marginBottom: 24, border: "1px solid #e5e7eb" }} elevation={0}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Typography style={{ fontWeight: 600, fontSize: 14 }}>Tokens de IA</Typography>
            <Typography style={{ fontSize: 13, color: "#6b7280" }}>
              {stats.tokens.used.toLocaleString()} / {stats.tokens.limit.toLocaleString()}
            </Typography>
          </div>
          <LinearProgress variant="determinate" value={tokenPercent} className={classes.tokenBar} />
        </Paper>
      )}

      {/* Main Cards */}
      <Typography className={classes.sectionTitle}>Herramientas principales</Typography>
      <Grid container spacing={2}>
        {cards.slice(0, 3).map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.path}>
            <Paper className={classes.card} elevation={0} onClick={() => history.push(card.path)}>
              <div className={classes.cardIcon} style={{ background: card.color }}>
                {card.icon}
              </div>
              <Typography className={classes.cardTitle}>{card.title}</Typography>
              <Typography className={classes.cardDescription}>{card.description}</Typography>
              {card.value !== undefined && (
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span className={classes.cardValue}>{card.value}</span>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>{card.valueLabel}</span>
                </div>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography className={classes.sectionTitle}>Marketing y automatizacion</Typography>
      <Grid container spacing={2}>
        {cards.slice(3, 7).map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.path}>
            <Paper className={classes.card} elevation={0} onClick={() => history.push(card.path)}>
              <div className={classes.cardIcon} style={{ background: card.color }}>
                {card.icon}
              </div>
              <Typography className={classes.cardTitle}>{card.title}</Typography>
              <Typography className={classes.cardDescription}>{card.description}</Typography>
              {card.value !== undefined && (
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span className={classes.cardValue}>{card.value}</span>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>{card.valueLabel}</span>
                </div>
              )}
              {card.extra && (
                <span className={classes.statChip}>{card.extra}</span>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography className={classes.sectionTitle}>Administracion</Typography>
      <Grid container spacing={2}>
        {cards.slice(7).map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.path}>
            <Paper className={classes.card} elevation={0} onClick={() => history.push(card.path)}>
              <div className={classes.cardIcon} style={{ background: card.color }}>
                {card.icon}
              </div>
              <Typography className={classes.cardTitle}>{card.title}</Typography>
              <Typography className={classes.cardDescription}>{card.description}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default ClientDashboard;
